import React from 'react';
import type { Room } from '@deadpan/shared';
import type { Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents } from '@deadpan/shared';
import { MIN_PLAYERS_FOR_WORDSMITH } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { useSocket } from '../../contexts/SocketContext';

interface UndercoverConfigProps {
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  currentRoom: Room;
  undercoverCount: number;
  blankCount: number;
  mode: 'classic' | 'ghost';
  factInputSeconds: number;
  onUndercoverCountChange: (val: number) => void;
  onBlankCountChange: (val: number) => void;
  onModeChange: (mode: 'classic' | 'ghost') => void;
  onFactInputSecondsChange: (seconds: number) => void;
  /** While true, RoomLobby will not overwrite local seconds from server (avoids 3→30 then “0”→300→180). */
  onFactTimerEditStart?: () => void;
  onFactTimerEditEnd?: () => void;
  configError: string | null;
}

export const UndercoverConfig: React.FC<UndercoverConfigProps> = ({
  socket,
  currentRoom,
  undercoverCount,
  blankCount,
  mode,
  factInputSeconds,
  onUndercoverCountChange,
  onBlankCountChange,
  onModeChange,
  onFactInputSecondsChange,
  onFactTimerEditStart,
  onFactTimerEditEnd,
  configError,
}) => {
  const t = useTranslations();
  const { myPlayerId } = useSocket();
  const totalPlayers = currentRoom.players.length;
  // Wordsmith is excluded from role assignment (see UndercoverGameManager)
  const hasWordsmith = !!currentRoom.undercoverConfig?.wordsmithId;
  const playersForRoleAssignment = hasWordsmith ? totalPlayers - 1 : totalPlayers;
  const civilianCount = playersForRoleAssignment - undercoverCount - blankCount;
  const isGhostMode = mode === 'ghost';

  const handleUndercoverConfigChange = (type: 'undercover' | 'blank', value: number) => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    
    // Allow any input, including negative values or values that exceed total players
    // Validation will be done when starting the game
    const numValue = isNaN(value) ? 0 : value;
    
    if (type === 'undercover') {
      onUndercoverCountChange(numValue);
    } else {
      onBlankCountChange(numValue);
    }
    
    socket.emit('client:update-undercover-config', 
      type === 'undercover' ? numValue : undercoverCount,
      type === 'blank' ? numValue : blankCount,
      currentRoom.undercoverConfig?.wordsmithId
    );
  };

  return (
    <>
      {/* Wordsmith Assignment (classic only) */}
      {!isGhostMode && (
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-pink-50 border-2 border-pink-200 rounded-xl">
        <h3 className="text-base sm:text-lg font-bold text-pink-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span>✍️</span>
          {t.games.deadpan.assignWordsmith}
        </h3>
        {totalPlayers < MIN_PLAYERS_FOR_WORDSMITH && (
          <p className="text-sm text-pink-700 mb-4">{t.games.deadpan.wordsmithRequired}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {currentRoom.players.map((player) => {
            const canAssign = totalPlayers >= MIN_PLAYERS_FOR_WORDSMITH;
            const isAssigned = currentRoom.undercoverConfig?.wordsmithId === player.id;
            return (
              <button
                key={player.id}
                onClick={() => {
                  if (socket && canAssign) {
                    socket.emit('client:update-undercover-config', 
                      undercoverCount, 
                      blankCount, 
                      isAssigned ? undefined : player.id
                    );
                  }
                }}
                disabled={!canAssign}
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  isAssigned
                    ? 'border-pink-500 bg-pink-100 shadow-md'
                    : canAssign
                    ? 'border-pink-200 bg-white hover:border-pink-300 hover:shadow-sm'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">{player.name}</span>
                  {isAssigned && (
                    <span className="text-pink-600 font-bold">✓</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {currentRoom.undercoverConfig?.wordsmithId && totalPlayers >= MIN_PLAYERS_FOR_WORDSMITH && (
          <button
            onClick={() => {
              if (socket && currentRoom.undercoverConfig?.wordsmithId) {
                socket.emit('client:update-undercover-config', 
                  undercoverCount, 
                  blankCount, 
                  undefined
                );
              }
            }}
            className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
          >
            {t.games.deadpan.removeWordsmith}
          </button>
        )}
      </div>
      )}

      {/* Undercover Game Configuration */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
        <h3 className="text-base sm:text-lg font-bold text-amber-800 mb-3 sm:mb-4 flex items-center gap-2">
          <span>⚙️</span>
          {t.games.undercover.undercoverConfig || '角色配置'}
        </h3>
        {configError && (
          <div className="mb-4 p-3 bg-red-100 border-2 border-red-300 rounded-lg">
            <p className="text-sm font-semibold text-red-800">{configError}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.games.undercover.ghostModeLabel}
            </label>
            <select
              value={mode}
              onChange={(e) => {
                const nextMode = e.target.value as 'classic' | 'ghost';
                onModeChange(nextMode);
                socket?.emit('client:update-undercover-mode', nextMode);
                if (nextMode === 'ghost') {
                  onBlankCountChange(0);
                  const nextUc = Math.max(1, undercoverCount);
                  onUndercoverCountChange(nextUc);
                  socket?.emit('client:update-undercover-config', nextUc, 0, undefined);
                }
              }}
              className="w-full px-4 py-2 border-2 border-amber-300 rounded-lg focus:outline-none focus:border-amber-500"
            >
              <option value="classic">{t.games.undercover.ghostModeClassic}</option>
              <option value="ghost">{t.games.undercover.ghostModeNoTrace}</option>
            </select>
          </div>
          {isGhostMode && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.games.undercover.ghostFactTimerLabel}
              </label>
              <input
                type="number"
                min={30}
                max={180}
                value={factInputSeconds}
                onFocus={() => onFactTimerEditStart?.()}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    onFactInputSecondsChange(0);
                    return;
                  }
                  const val = parseInt(raw, 10);
                  if (!Number.isNaN(val)) {
                    onFactInputSecondsChange(val);
                  }
                }}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  let n = raw === '' ? 60 : parseInt(raw, 10);
                  if (Number.isNaN(n)) n = 60;
                  const clamped = Math.max(30, Math.min(180, n));
                  onFactInputSecondsChange(clamped);
                  socket?.emit('client:update-undercover-fact-timer', clamped);
                  onFactTimerEditEnd?.();
                }}
                className="w-full px-4 py-2 border-2 border-amber-300 rounded-lg focus:outline-none focus:border-amber-500"
              />
            </div>
          )}
          {isGhostMode && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.games.undercover.undercoverCount || '卧底数量'}
                </label>
                <input
                  type="number"
                  min={1}
                  value={undercoverCount}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0;
                    handleUndercoverConfigChange('undercover', val);
                  }}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    configError ? 'border-red-500 focus:border-red-600' : 'border-amber-300 focus:border-amber-500'
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.games.undercover.civilianCount || '平民人数'}:{' '}
                  <span className={totalPlayers - undercoverCount <= undercoverCount ? 'text-red-600' : ''}>
                    {totalPlayers - undercoverCount}
                  </span>
                </label>
              </div>
              <p className="text-sm text-amber-900">{t.games.undercover.ghostNoBlankRoleLine}</p>
            </>
          )}
          {!isGhostMode && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.games.undercover.undercoverCount || 'Undercover Count'}
                </label>
                <input
                  type="number"
                  value={undercoverCount}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleUndercoverConfigChange('undercover', val);
                  }}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    configError ? 'border-red-500 focus:border-red-600' : 'border-amber-300 focus:border-amber-500'
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.games.undercover.blankCount || 'Blank Count'}
                </label>
                <input
                  type="number"
                  value={blankCount}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleUndercoverConfigChange('blank', val);
                  }}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    configError ? 'border-red-500 focus:border-red-600' : 'border-amber-300 focus:border-amber-500'
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.games.undercover.civilianCount || 'Civilian Count'}: <span className={civilianCount < 0 ? 'text-red-600' : ''}>{civilianCount}</span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

